import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  X,
  Search,
  Plus,
  Pencil,
  ChevronDown,
  User,
  Wrench,
  FileText,
  Tag,
  Loader2,
  Calendar,
  CheckCircle,
} from "lucide-react";
import { base_url } from "../../../config/config";

const ITEMS_PER_PAGE = 20;

const AVAILABLE_PROCESSES = [
  {
    id: 1,
    label:
      "Materialeingang / Materialausgang mit Kontrolle und Weitergabe an die Produktion incl. Auftragsnummer",
  },
  { id: 2, label: "Baugruppe montieren" },
  { id: 3, label: "Maschine einrichten" },
  { id: 4, label: "CNC Fräsen" },
  { id: 5, label: "Konventionell Fräsen" },
  { id: 6, label: "CNC Drehen" },
  { id: 7, label: "Konventionell Drehen" },
  { id: 8, label: "Schweißen" },
  { id: 9, label: "Sägen" },
  { id: 10, label: "Konservieren / Lackieren" },
  { id: 11, label: "Verpacken / Versenden" },
  { id: 12, label: "Transport innerhalb der Firma" },
  { id: 13, label: "Qualitätskontrolle" },
  { id: 14, label: "Sonstiges" },
  { id: 15, label: "Angaben Formulare zu re übergeben" },
  { id: 16, label: "Stoßen" },
  { id: 17, label: "Normalisieren / Härten" },
  { id: 18, label: "Löten" },
  { id: 0, label: "Unterbrechung", isBreak: true },
];

const PROCESS_COLORS = {
  1: "#005B96",
  2: "orange",
  3: "teal",
  4: "#1FD655",
  5: "#649200",
  6: "#a2afb0",
  7: "grey",
  8: "#95CBE2",
  9: "#844204",
  10: "#680000",
  11: "#E9D700",
  12: "#1E64AF",
  13: "green",
  14: "#557882",
  15: "pink",
  16: "#60CDB8",
  17: "#373737",
  18: "#B87333",
  0: "#FFC107",
};

// Searchable Dropdown Component
const SearchableDropdown = ({
  label,
  icon: Icon,
  options,
  value,
  onChange,
  displayKey,
  searchKeys,
  placeholder,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter((opt) =>
      searchKeys.some((key) =>
        opt[key]?.toString().toLowerCase().includes(term),
      ),
    );
  }, [options, searchTerm, searchKeys]);

  const selectedDisplay = value
    ? typeof displayKey === "function"
      ? displayKey(value)
      : value[displayKey]
    : "";

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <span className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-gray-500" />}
          {label}
        </span>
      </label>
      <div
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 cursor-pointer bg-white flex items-center justify-between hover:border-blue-400 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        {loading ? (
          <span className="flex items-center gap-2 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Laden...
          </span>
        ) : (
          <span className={value ? "text-gray-900 truncate" : "text-gray-400"}>
            {selectedDisplay || placeholder}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                Keine Ergebnisse
              </div>
            ) : (
              filteredOptions.map((option, idx) => (
                <div
                  key={option._id || option.id || idx}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50`}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  {typeof displayKey === "function"
                    ? displayKey(option)
                    : option[displayKey]}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Process Type Dropdown with Colors
const ProcessTypeDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <span className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-gray-500" />
          Prozess Typ
        </span>
      </label>
      <div
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 cursor-pointer bg-white flex items-center justify-between hover:border-blue-400 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        {value ? (
          <span className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: PROCESS_COLORS[value.id] }}
            />
            <span className="text-gray-900 truncate">{value.label}</span>
          </span>
        ) : (
          <span className="text-gray-400">Prozess auswählen...</span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {AVAILABLE_PROCESSES.map((process) => (
            <div
              key={process.id}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center gap-2 ${
                value?.id === process.id ? "bg-blue-100" : ""
              }`}
              onClick={() => {
                onChange(process);
                setIsOpen(false);
              }}
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: PROCESS_COLORS[process.id] }}
              />
              <span className={`truncate ${process.isBreak ? "italic" : ""}`}>
                {process.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * AddProcessModal - Modal for adding or editing a process
 */
const AddProcessModal = ({
  isOpen,
  onClose,
  onAdd,
  onUpdate,
  selectedDate,
  editingProcess,
}) => {
  const [formData, setFormData] = useState({
    processType: null,
    machine: null,
    assignee: null,
    anr: null,
    hours: 8,
  });

  // API Data State
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingMachines, setLoadingMachines] = useState(false);

  // Projects/ANR State with pagination
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProjects, setTotalProjects] = useState(0);

  const listRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    "all",
    (currentYear - 1).toString(),
    currentYear.toString(),
  ];

  // Fetch employees and machines when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      fetchMachines();
      fetchProjects(1, true);
    }
  }, [isOpen]);

  // Populate form when editing
  useEffect(() => {
    if (
      isOpen &&
      editingProcess &&
      employees.length > 0 &&
      machines.length > 0
    ) {
      // Find matching process type
      const matchingProcessType = AVAILABLE_PROCESSES.find(
        (p) =>
          p.label === editingProcess.label ||
          p.id.toString() === editingProcess.id?.toString(),
      );

      // Find matching employee
      const matchingEmployee = employees.find(
        (emp) =>
          emp.name === editingProcess.names?.[0] ||
          emp._id === editingProcess.assignedEmployees?.[0]?.id,
      );

      // Find matching machine
      const matchingMachine = machines.find(
        (m) =>
          m.name === editingProcess.machinename ||
          m.name === editingProcess.assignedEmployees?.[0]?.machine?.name,
      );

      setFormData({
        processType: matchingProcessType || null,
        machine: matchingMachine || null,
        assignee: matchingEmployee || null,
        anr: editingProcess.anr
          ? {
              anr: editingProcess.anr,
              auftragsbezeichnung: editingProcess.auftragsbezeichnung || "",
              projectId: editingProcess.projectId,
            }
          : null,
        hours: parseFloat(editingProcess.hours) || 8,
      });
    } else if (isOpen && !editingProcess) {
      // Reset form for new process
      setFormData({
        processType: null,
        machine: null,
        assignee: null,
        anr: null,
        hours: 8,
      });
    }
  }, [isOpen, editingProcess, employees, machines]);

  // Debounced search for projects
  useEffect(() => {
    if (!isOpen) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, year]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await fetch(`${base_url}/v1/getAllEmployees`);
      const data = await response.json();
      const activeEmployees = data.filter((emp) => emp.isActive !== false);
      setEmployees(activeEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchMachines = async () => {
    setLoadingMachines(true);
    try {
      const response = await fetch(`${base_url}/v1/getAllMachines`);
      const data = await response.json();
      setMachines(data);
    } catch (error) {
      console.error("Error fetching machines:", error);
    } finally {
      setLoadingMachines(false);
    }
  };

  const fetchProjects = useCallback(
    async (pageNum = 1, isNewSearch = false) => {
      if (isNewSearch) {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: ITEMS_PER_PAGE.toString(),
          sortField: "createdAt",
          sortOrder: "desc",
        });

        if (searchTerm.trim()) {
          params.append("search", searchTerm.trim());
        }
        if (year && year !== "all") {
          params.append("year", year);
        }

        const response = await fetch(
          `${base_url}/v1/getAllProjects?${params.toString()}`,
        );
        const responseData = await response.json();

        if (responseData.status === 1) {
          const fetchedProjects = responseData.data || [];
          const total = responseData.totalProjects || fetchedProjects.length;

          setTotalProjects(total);

          if (isNewSearch || pageNum === 1) {
            setProjects(fetchedProjects);
          } else {
            setProjects((prev) => [...prev, ...fetchedProjects]);
          }

          setHasMore(fetchedProjects.length === ITEMS_PER_PAGE);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, year],
  );

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      setLoading(true);
      fetchProjects(nextPage, false);
    }
  }, [loading, hasMore, page, fetchProjects]);

  // Attach scroll listener
  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
      return () => listElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleSelectProject = (project) => {
    setFormData({
      ...formData,
      anr: {
        anr: project.ANR,
        auftragsbezeichnung: project.Auftragsbezeichnung,
        projectId: project._id,
      },
    });
  };

  const handleSubmit = async () => {
    if (
      !formData.anr ||
      !formData.processType ||
      !formData.assignee ||
      !formData.machine
    ) {
      return;
    }

    // DEBUG: Log what we're getting from editingProcess
    console.log("🔍 Edit Process Debug:", {
      editingProcess: editingProcess,
      hasInstanceId: !!editingProcess?.instanceId,
      instanceId: editingProcess?.instanceId,
      uniqueId: editingProcess?.uniqueId,
      _id: editingProcess?._id,
    });

    // Use existing instanceId if editing, otherwise generate new one
    const instanceId =
      editingProcess?.instanceId ||
      `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const chainId = editingProcess?.chainId || instanceId;

    const processObject = {
      ...(editingProcess?._id && { _id: editingProcess._id }),
      id: formData.processType.id.toString(),
      instanceId: instanceId,
      label: formData.processType.label,
      originalLabel:
        editingProcess?.originalLabel || formData.processType.label,
      hours: parseFloat(formData.hours) || 8,
      names: [formData.assignee.name],
      machinename: formData.machine.name,
      assignedEmployees: [
        {
          id: formData.assignee._id,
          name: formData.assignee.name,
          machine: {
            name: formData.machine.name,
            prozess: formData.machine.prozess || "",
            typeOfMachine: formData.machine.typeOfMachine || "",
            manufacture: formData.machine.manufacture || "",
            ranking: formData.machine.ranking || 0,
            image: formData.machine.image || "",
          },
          machineData: formData.machine.name,
          role: "",
        },
      ],
      anr: formData.anr.anr,
      auftragsbezeichnung: formData.anr.auftragsbezeichnung || "",
      projectId: formData.anr.projectId,
      scheduledDate: selectedDate,
      chainId: chainId,
      isChained: editingProcess?.isChained || false,
      nextProcessId: editingProcess?.nextProcessId || null,
      chainOrder: editingProcess?.chainOrder || 0,
      type: "process",
      color: PROCESS_COLORS[formData.processType.id] || "#1976d2",
      isMultiDay: editingProcess?.isMultiDay || false,
      dayNumber: editingProcess?.dayNumber || 1,
      totalDays: editingProcess?.totalDays || 1,
      hoursForThisDay: editingProcess?.hoursForThisDay || 0,
      originalProcessId:
        editingProcess?.originalProcessId || formData.processType.id.toString(),

      // Instance metadata - preserve existing values
      isInstance: editingProcess?.isInstance || false,
      instanceNumber: editingProcess?.instanceNumber || 1,
      globalIndex: editingProcess?.globalIndex || 0,
    };

    try {
      const apiEndpoint = editingProcess
        ? `${base_url}/v1/UpdateProjectProcess`
        : `${base_url}/v1/CreateProjectPlanning`;

      const method = editingProcess ? "PUT" : "POST";

      const response = await fetch(apiEndpoint, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processes: [processObject],
          tasks: [],
          projectId: formData.anr.projectId,
        }),
      });

      const data = await response.json();

      if (data.status === 1) {
        if (!editingProcess) {
          const localProcess = {
            ...processObject,
            uniqueId: instanceId,
            date: selectedDate,
            type: "process",
          };

          onAdd(localProcess);
        } else {
          // Handle update
          const localProcess = {
            ...processObject,
            uniqueId: editingProcess.uniqueId, // Keep original uniqueId
            date: selectedDate,
            type: "process",
            _id: editingProcess._id, // Ensure _id is passed back
          };

          if (onUpdate) {
            onUpdate(localProcess);
          }
        }

        setFormData({
          processType: null,
          machine: null,
          assignee: null,
          anr: null,
          hours: 8,
        });
        setSearchTerm("");
        setYear(currentYear.toString());
        onClose();
      } else {
        alert("Fehler beim Speichern des Prozesses: " + data.message);
      }
    } catch (error) {
      alert(
        "Fehler beim Speichern des Prozesses. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const handleClose = () => {
    setFormData({
      processType: null,
      machine: null,
      assignee: null,
      anr: null,
      hours: 8,
    });
    setSearchTerm("");
    setYear(currentYear.toString());
    setProjects([]);
    onClose();
  };

  if (!isOpen) return null;

  const formattedDate = selectedDate
    ? new Date(selectedDate).toLocaleDateString("de-DE", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="flex h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

        <div className="relative w-full max-w-3xl max-h-[85vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all flex flex-col">
          {/* Header */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-white/10 p-2">
                  {editingProcess ? (
                    <Pencil className="h-6 w-6 text-white" />
                  ) : (
                    <Plus className="h-6 w-6 text-white" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {editingProcess
                      ? "Prozess bearbeiten"
                      : "Neuen Prozess hinzufügen"}
                  </h2>
                  <p className="mt-0.5 text-sm text-blue-100">
                    {formattedDate}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="rounded-lg p-2 text-white hover:bg-white/10 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Form - scrollable body */}
          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {/* Row 1: Employee and Machine */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Assignee */}
              <SearchableDropdown
                label="Mitarbeiter"
                icon={User}
                options={employees}
                value={formData.assignee}
                onChange={(val) => setFormData({ ...formData, assignee: val })}
                displayKey="name"
                searchKeys={["name", "email"]}
                placeholder="Mitarbeiter auswählen..."
                loading={loadingEmployees}
              />

              {/* Machine */}
              <SearchableDropdown
                label="Maschine"
                icon={Wrench}
                options={machines}
                value={formData.machine}
                onChange={(val) => setFormData({ ...formData, machine: val })}
                displayKey={(opt) =>
                  `${opt.name} - ${opt.prozess || opt.typeOfMachine || ""}`
                }
                searchKeys={["name", "prozess", "typeOfMachine", "manufacture"]}
                placeholder="Maschine auswählen..."
                loading={loadingMachines}
              />
            </div>

            {/* Process Type and Hours */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ProcessTypeDropdown
                value={formData.processType}
                onChange={(val) =>
                  setFormData({ ...formData, processType: val })
                }
              />

              {/* Hours Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-2">
                    <svg
                      className="h-4 w-4 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Stunden
                  </span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="8"
                  step="0.5"
                  value={formData.hours}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    const clampedValue = Math.max(1, Math.min(8, value));
                    setFormData({ ...formData, hours: clampedValue });
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Stunden eingeben..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  1-8 Std. erlaubt (8 Std. = volle Tagesbreite)
                </p>
              </div>
            </div>

            {/* ANR Selection Section */}
            <div className="border-t border-gray-200 pt-5">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  ANR / Projekt auswählen
                </span>
              </label>

              {/* Selected ANR Display */}
              {formData.anr && (
                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <span className="font-semibold text-green-800">
                      {formData.anr.anr}
                    </span>
                    {formData.anr.auftragsbezeichnung && (
                      <span className="text-green-700 ml-2">
                        - {formData.anr.auftragsbezeichnung}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setFormData({ ...formData, anr: null })}
                    className="text-green-600 hover:text-green-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Search and Year Filter */}
              <div className="flex gap-3 mb-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="ANR oder Bezeichnung suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                  )}
                </div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white appearance-none cursor-pointer"
                  >
                    <option value="all">Alle Jahre</option>
                    {yearOptions
                      .filter((y) => y !== "all")
                      .map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Results count */}
              {totalProjects > 0 && (
                <div className="text-xs text-gray-500 mb-2">
                  {projects.length} von {totalProjects} Projekten angezeigt
                </div>
              )}

              {/* Projects List */}
              <div
                ref={listRef}
                className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg"
              >
                {loading && projects.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 text-blue-500 animate-spin mr-2" />
                    <span className="text-gray-500">
                      Projekte werden geladen...
                    </span>
                  </div>
                ) : projects.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    Keine Projekte gefunden
                  </div>
                ) : (
                  <>
                    {projects.map((project, index) => (
                      <div
                        key={`${project._id}-${index}`}
                        onClick={() => handleSelectProject(project)}
                        className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                          formData.anr?.anr === project.ANR
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {project.ANR}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {project.Auftragsbezeichnung ||
                                "Keine Beschreibung"}
                            </div>
                          </div>
                          {formData.anr?.anr === project.ANR && (
                            <CheckCircle className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Loading more indicator */}
                    {loading && projects.length > 0 && (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                      </div>
                    )}

                    {/* End of list */}
                    {!hasMore && projects.length > 0 && (
                      <div className="text-center py-2 text-xs text-gray-400">
                        Keine weiteren Projekte
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  !formData.anr ||
                  !formData.processType ||
                  !formData.assignee ||
                  !formData.machine
                }
                className={`rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors ${
                  formData.anr &&
                  formData.processType &&
                  formData.assignee &&
                  formData.machine
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProcessModal;
